// ccw 2026-09-28 msl-map: Liaison card for the MSL map. The whole card is the hover/tap/focus
// target; the email link keeps working inside it. Each card carries a visually hidden list of
// its states, so the cards are the accessible equivalent of the (aria-hidden) map.
import React, { useState } from 'react';

// One territory: solid ring. Two or more: equal conic-gradient slices in config order, built
// here so the ring returns to solid automatically when a territory is reassigned.
const ringBackground = (territories) => {
	if (territories.length === 0) return 'transparent'
	if (territories.length === 1) return territories[0].color
	const slice = 360 / territories.length
	return 'conic-gradient(' + territories.map((t, i) => t.color + ' ' + (i * slice) + 'deg ' + ((i + 1) * slice) + 'deg').join(', ') + ')'
}

// Split on any whitespace: the MSL table has non-breaking spaces in some names ("Angela&nbsp;Huff").
const initialsOf = (name) => (name || '').split(/\s+/).filter(Boolean).map(w => w[0]).join('')

// Keyboard focus shows :focus-visible; mouse clicks and taps don't, so pointer and touch
// focus are left to the hover and tap handlers.
const isKeyboardFocus = (el) => {
	try {
		return el.matches(':focus-visible')
	} catch (err) {
		return true
	}
}

const MslCard = (props) => {
	const { liaison, stateName, isFocused, fncPointerEnter, fncPointerLeave, fncKeyboardFocus, fncKeyboardBlur, fncTap, fncEmailClick } = props
	const [photoFailed, setPhotoFailed] = useState(false)

	const firstColor = liaison.territories.length ? liaison.territories[0].color : '#00BCB4'
	const coveredStates = []
	liaison.territories.forEach(t => t.states.forEach(s => coveredStates.push(stateName[s] || s)))

	const onFocus = (e) => {
		if (isKeyboardFocus(e.target)) fncKeyboardFocus(liaison.id)
	}

	const onBlur = (e) => {
		// Moving focus between the card and its own email link keeps the highlight.
		if (e.relatedTarget && e.currentTarget.contains(e.relatedTarget)) return
		fncKeyboardBlur(liaison.id)
	}

	const onClick = (e) => {
		if (e.target.closest && e.target.closest('a')) return
		fncTap(liaison.id)
	}

	return (
		<div
			className={'msl-card' + (isFocused ? ' is-focused' : '') + (liaison.territories.length ? '' : ' is-static')}
			tabIndex={liaison.territories.length ? 0 : undefined}
			data-liaison={liaison.id}
			style={{ '--msl-card-color': firstColor }}
			onMouseEnter={() => fncPointerEnter(liaison.id)}
			onMouseLeave={fncPointerLeave}
			onFocus={onFocus}
			onBlur={onBlur}
			onClick={onClick}
		>
			<div className="msl-ring" style={{ background: ringBackground(liaison.territories) }}>
				{liaison.photo && !photoFailed
					? <img src={'/images/msl/' + liaison.photo} alt={liaison.name} onError={() => setPhotoFailed(true)} />
					: <div className="msl-photo-fallback" role="img" aria-label={liaison.name}>{initialsOf(liaison.name)}</div>
				}
			</div>
			<div className="msl-card-info">
				{/* ccw 2026-09-28 msl-map: compact card (client feedback) — credentials share the name line. */}
				<span className="msl-card-name">
					{liaison.name}
					{liaison.degree && <span className="msl-card-degree">, {liaison.degree}</span>}
				</span>
				{liaison.title && <span className="msl-card-title">{liaison.title}</span>}
				{liaison.territories.length > 0
					? liaison.territories.map(t => (
						<span key={t.id} className="msl-card-region">
							<span className="msl-dot" style={{ background: t.color }}></span>
							{t.label}{t.note ? ' (' + t.note + ')' : ''}
						</span>
					))
					: liaison.region && <span className="msl-card-region">{liaison.region}</span>
				}
				{liaison.email &&
					<a href={'mailto:' + liaison.email} onClick={() => fncEmailClick(liaison.id)}>
						{/* Let narrow cards wrap after the @ rather than mid-word. */}
						{liaison.email.split('@')[0]}@<wbr />{liaison.email.split('@').slice(1).join('@')}
					</a>
				}
				{coveredStates.length > 0 &&
					<span className="msl-sr-only">Covers: {coveredStates.join(', ')}</span>
				}
			</div>
		</div>
	)
}

export default MslCard
